import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { X, Loader2, Camera as CameraIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Live camera barcode scanner. Uses ZXing to decode UPC-A / EAN-13 / EAN-8 / CODE-128
 * from the rear-facing camera and calls onDetected(code) with the first stable read.
 */
const BarcodeScannerModal = ({ open, onClose, onDetected }) => {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [status, setStatus] = useState('starting');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });

    const start = async () => {
      try {
        setStatus('starting');
        setError(null);
        // Ask for rear camera explicitly
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result) {
              const code = result.getText();
              if (code && /^\d{6,14}$/.test(code.trim())) {
                controls.stop();
                try { if (navigator.vibrate) navigator.vibrate([80, 30, 80]); } catch { /* ignore */ }
                setStatus('detected');
                setTimeout(() => onDetected(code.trim()), 320);
              }
            }
          },
        );
        controlsRef.current = controls;
        if (!cancelled) setStatus('scanning');
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.name === 'NotAllowedError'
            ? 'Camera access denied. Enable camera permission and try again.'
            : e?.message || 'Could not open camera',
        );
        setStatus('error');
      }
    };
    start();

    return () => {
      cancelled = true;
      try {
        controlsRef.current?.stop();
      } catch {
        /* ignore */
      }
      controlsRef.current = null;
    };
  }, [open, onDetected]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="barcode-scanner-modal"
    >
      <div className="w-full max-w-md bg-[#FAF9F6] rounded-3xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1D8]">
          <div className="flex items-center gap-2">
            <CameraIcon className="w-4 h-4 text-[#4A7C59]" strokeWidth={1.5} />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">
              Scan a barcode
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#F2EFE9] flex items-center justify-center text-[#4A5A53]"
            data-testid="barcode-scanner-close"
            aria-label="Close scanner"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="relative aspect-[4/3] bg-black">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
            data-testid="barcode-scanner-video"
          />

          {status === 'starting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm font-light">Opening camera…</span>
            </div>
          )}

          {status === 'scanning' && (
            <>
              {/* Reticle */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-[78%] h-24 border-2 border-white/80 rounded-2xl relative">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-[#4A7C59] shadow-[0_0_12px_rgba(74,124,89,0.9)] animate-pulse" />
                </div>
              </div>
              <div className="absolute bottom-3 inset-x-0 text-center text-white/90 text-xs font-light">
                Align the barcode inside the frame
              </div>
            </>
          )}

          {status === 'detected' && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-[#4A7C59]/70 backdrop-blur-[2px] animate-in fade-in"
              data-testid="barcode-scanner-success"
            >
              <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-[#4A7C59]" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="px-5 py-4 text-sm text-[#C05746]" data-testid="barcode-scanner-error">
            {error}
          </div>
        )}

        <div className="px-5 py-4 flex justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            className="rounded-full border-[#E5E1D8]"
            data-testid="barcode-scanner-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
